import { Injectable } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AssetSearchModalComponent } from "../components/asset-search-modal/asset-search-modal.component";
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap, tap } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from 'src/environments/environment';
import { TYPESENSE_COLLECTIONS } from "../constants/typesense-collections";
import { CollectionComplianceService } from "./collection-compliance.service";
import { AuthService } from "./auth.service";
import { SubscriptionService } from "./subscription.service";

export interface SearchResult {
   stockName: string;
   companyName?: string;
   logo: string | null;
   type: "STOCK" | "ETF" | "IPO";
   exchange?: string;
   countryCode?: string;
   countryName?: string;
   flag?: string;
   complianceStatus: string;
   complianceRating: string | number;
   isStatusLocked?: boolean;
   isAccessible?: boolean;
   isRatingLocked?: boolean;
   textMatch: number;
}

export interface EtfProfile {
   symbol: string;
   id: string;
   name: string;
   exchange: string;
   domicile: string;
   aum: number;
   nav: number;
   navCurrency: string;
   expenseRatio: number;
   assetClass: string;
   etfCompany: string;
   inceptionDate: string;
   numberOfHoldings: number;
   trackingIndex: string;

   halal?: boolean;
   notHalal?: boolean;
   avoid?: boolean;
   unCovered?: boolean;

   cusip?: string;
   isin?: string;
   identifier?: string;
   mainSymbol?: string;
   shariahStates?: string;
   investmentSegment?: string;
   ranking?: number;
   description?: string;
}

@Injectable({ providedIn: "root" })
export class AssetSearchModalService {
   private modalRef?: NgbModalRef;

   private _cachedTopStocks$?: Observable<SearchResult[]>;
   private _cachedTopEtfs$?: Observable<SearchResult[]>;

   constructor(
      private modal: NgbModal,
      private http: HttpClient,
      private collectionCompliance: CollectionComplianceService,
      private authService: AuthService,
      private subscriptionService: SubscriptionService,
   ) { }

   open(): void {
      if (this.modalRef) return;

      this.modalRef = this.modal.open(AssetSearchModalComponent, {
         size: "lg",
         animation: false,
         windowClass: "stocks-etfs-search-modal",
         centered: true
      });

      this.modalRef.result.finally(() => {
         this.modalRef = undefined;
      });
   }

   close(): void {
      this.modalRef?.close();
      this.modalRef = undefined;
   }

   searchStocksAndEtfs( name: string, stockCountries: any = [], etfCountries: any = [], userCountry: string | null ): any {
      if (name === '') {
         return of([]);
      }

      const searches = [
         {
            collection: TYPESENSE_COLLECTIONS.COMPANY_PROFILE,
            q: name,
            query_by: 'name,ticker',
            prioritize_token_position: true,
            sort_by: `_text_match:desc,$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(isMainTicker:desc,usdMarketCap:desc)`,
            query_by_weights: '1,2',
            filter_by: `$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(status:=PUBLISH&&country:=[${stockCountries}])`,
            include_fields: `*,$${TYPESENSE_COLLECTIONS.STOCKS_DATA}(id,sharia_compliance,ranking,ranking_v2)`
         },
         {
            collection: TYPESENSE_COLLECTIONS.ETF_PROFILE,
            q: name,
            query_by: 'name,symbol',
            prioritize_token_position: true,
            sort_by: `_text_match:desc,$${TYPESENSE_COLLECTIONS.ETFS_DATA}(aum:desc)`,
            query_by_weights: '1,2',
            filter_by: `$${TYPESENSE_COLLECTIONS.ETFS_DATA}(domicile:=[${etfCountries}])`,
            include_fields: `*,$${TYPESENSE_COLLECTIONS.ETFS_DATA}(id,aum,domicile,shariahCompliantStatus,ranking_v2,logo)`
         },
      ];
      return this.http
         .post<any>(environment.typeSense.url + '/multi_search', { searches })
         .pipe(
            switchMap(item => {
               const results: any = item?.results;
               const ret: SearchResult[] = [];

               if (results?.[0]?.hits.length > 0) {
                  this.processStockData(results[0].hits, ret, name);
               }
               if (results?.[1]?.hits.length > 0) {
                  this.processEtfData(results[1].hits, ret, name);
               }

               return this.applyComplianceAccess(ret);
            }),
            map(ret => {
               ret.sort((a, b) => (b.textMatch ?? 0) - (a.textMatch ?? 0));
               return ret;
            })
         );
   }

   private processStockData(data: any, ret: any[], name: string) {
      data.forEach((x: any) => {
         const doc = x.document;
         const tickerSegments = doc.ticker.split('.');
         let textMatch = x.text_match;
         if (tickerSegments.includes(name.toUpperCase())) {
            textMatch += textMatch;
         }
         ret.push({
            canAddToWatchlist: true,
            companyName: doc.name,
            countryName: doc.country,
            exchange: doc.exchange,
            flag: (doc.exchange_country || doc.country)?.toLowerCase(),
            logo: doc.logo,
            identifier: doc.identifier,
            stockName: doc.ticker,
            type: 'STOCK',
            textMatch: textMatch,
            isin: doc.isin,
            mainTicker: doc.mainTicker,
            currency: doc.currency,
            complianceStatus: doc.shariahCompliantStatus,
            ranking: doc.ranking_v2,
            change: doc.change,
            price: doc.currentPrice,
         });
      });
   }

   private processEtfData(data: any, ret: any[], name: string) {
      data.forEach((x: any) => {
         const doc = x.document;
         const etfData = doc[TYPESENSE_COLLECTIONS.ETFS_DATA];
         const tickerSegments = doc.symbol.split('.');
         let textMatch = x.text_match;
         if (tickerSegments.includes(name)) {
            textMatch += textMatch;
         }
         ret.push({
            canAddToWatchlist: true,
            companyName: doc.name,
            countryName: doc.domicile,
            exchange: doc.exchange,
            flag: (doc.exchange_country || doc.domicile)?.toLowerCase(),
            logo: doc.logo || etfData?.logo,
            identifier: doc.identifier,
            stockName: doc.symbol,
            type: 'ETF',
            textMatch: textMatch,
            currency: doc.navCurrency,
            complianceStatus: doc.shariahStates,
            ranking: doc.ranking_v2,
            change: doc.change,
            price: doc.currentPrice,
         });
      });
   }

   private applyComplianceAccess( results: SearchResult[] ): Observable<SearchResult[]> {
   
      const stockSymbols = results
         .filter(r => r.type === 'STOCK')
         .map(r => r.stockName);
   
      const etfSymbols = results
         .filter(r => r.type === 'ETF')
         .map(r => r.stockName);
   
      return forkJoin({
         accessibleStocks: this.collectionCompliance.getAccessibleComplianceSymbols(
            stockSymbols,
            'stocks'
         ),
         accessibleEtfs: this.collectionCompliance.getAccessibleComplianceSymbols(
            etfSymbols,
            'etfs'
         )
      }).pipe(
         map(({ accessibleStocks, accessibleEtfs }) => {
            // console.log('[Asset Search] Accessible Symbols:', { accessibleStocks, accessibleEtfs });
   
            const role = SubscriptionService.subscriptionData.getValue()?.role ?? 'ANONYMOUS';
   
            const isLoggedIn = this.authService.isLoggedIn();
            const isPaidUser = role !== 'ANONYMOUS' && role !== 'FREEMIUM';
   
            return results.map(r => {
               const isFreeSymbol =
                  r.type === 'STOCK'
                     ? accessibleStocks.includes(r.stockName)
                     : accessibleEtfs.includes(r.stockName);
   
               // 🔐 STATUS LOGIC
               // - Free symbol → always visible
               // - Non-free → visible only if logged in
               const isStatusLocked =
                  !isFreeSymbol && !isLoggedIn;
   
               // 🔐 RATING LOGIC
               // - Free symbol → always visible
               // - Paid User → always visible
               // - Anonymous → Locked (if not free symbol)
               // - Logged In Free User → Locked ONLY if COMPLIANT
               let isRatingLocked = false;

               if (isFreeSymbol || isPaidUser) {
                  isRatingLocked = false;
               } else if (!isLoggedIn) {
                  isRatingLocked = true;
               } else {
                  isRatingLocked = r.complianceStatus === 'COMPLIANT';
               }
   
               return {
                  ...r,
                  isStatusLocked,
                  isRatingLocked,
                  isAccessible: isFreeSymbol,
               };
            });
         })
      );
   }

   getTopStocks(): Observable<SearchResult[]> {
      if (!this._cachedTopStocks$) {
         const url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.STOCKS_DATA}/documents/search`;

         const params = new HttpParams()
            .set('page', '1')
            .set('per_page', '20')
            .set('q', '*')
            .set(
               'filter_by',
               "$company_profile_collection_new(id:*)&&status:=PUBLISH&&country:=US&&exchange:=[`NYSE`,`NASDAQ`,`XASE`,`BATS`,`ARCX`]&&sharia_compliance:=[`COMPLIANT`,`NON_COMPLIANT`,`QUESTIONABLE`]"
            )
            .set('sort_by', 'usdMarketCap:desc');

         this._cachedTopStocks$ = this.http
            .get<any>(url, { params })
            .pipe(
               // tap(res => console.log('[Top Stocks - raw response]', res)),
               map(res => this.mapTopStocks(res)),
               // switchMap(stocks => this.applyComplianceAccess(stocks)), 
               // tap(data => console.log('[Top Stocks - mapped]', data)),
               shareReplay({ bufferSize: 1, refCount: true })
            );
      }

      return this._cachedTopStocks$.pipe(
         switchMap(stocks => this.applyComplianceAccess(stocks))
      );
   }

   private mapTopStocks(response: any): SearchResult[] {
      if (!response?.hits?.length) return [];
  
      return response.hits.map((hit: any): SearchResult => {
         const doc = hit.document;
   
         return {
            stockName: doc.company_profile_collection_new.ticker,
            companyName: doc.company_profile_collection_new.name,
            type: 'STOCK',
            exchange: doc.company_profile_collection_new.exchange,
            logo: doc.company_profile_collection_new.logo,
            countryCode: 'US',
            countryName: 'United States',
            flag: 'us',
            complianceStatus: doc.company_profile_collection_new.shariahCompliantStatus ?? '',
            complianceRating: doc.company_profile_collection_new.ranking_v2 ?? '',
            textMatch: 0,
         };
      });
   }

   getTopETFs(): Observable<SearchResult[]> {
      if (!this._cachedTopEtfs$) {
         const url = `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETFS_DATA}/documents/search`;

         const params = new HttpParams()
            .set("page", "1")
            .set("per_page", "20")
            .set("q", "*")
            .set(
               "filter_by",
               "domicile:=US&&exchange:=[`NYSE`,`NYSE ARCA`,`NASDAQ`,`BATS`]&&shariahCompliantStatus:=[`COMPLIANT`,`NON_COMPLIANT`,`QUESTIONABLE`]"
            )
            .set("sort_by", "aum:desc");

         this._cachedTopEtfs$ = this.http.get<any>(url, { params }).pipe(
            map(res => this.mapTopETFs(res)),
            switchMap(etfs => {
               if (!etfs.length) return of([]);

               const symbols = etfs.map(e => e.stockName);

               return this.getEtfDataFromSymbols(symbols).pipe(
                  map(profileMap =>
                     etfs.map(etf => {
                        const profile = profileMap[etf.stockName];
                        
                        return {
                           ...etf,
                           companyName: profile?.name ?? etf.companyName,
                           complianceStatus: String(profile.shariahStates ?? ''),
                           complianceRating: profile?.ranking ?? ''
                        };
                     })
                  )
               );
            }),
            // tap(finalEtfs => {
            //    console.log('[Top ETFs - final merged]', finalEtfs);
            // }),
            // switchMap(etfs => this.applyComplianceAccess(etfs)), 
            shareReplay({ bufferSize: 1, refCount: true })
         );
      }

      return this._cachedTopEtfs$.pipe(
         switchMap(etfs => this.applyComplianceAccess(etfs))
      );
   }

   private mapTopETFs(response: any): SearchResult[] {
      if (!response?.hits?.length) return [];

      return response.hits.map((hit: any): SearchResult => {
         const doc = hit.document;

         return {
            stockName: doc.symbol,
            companyName: doc.name,
            type: "ETF",
            exchange: doc.exchange,
            countryCode: "US",
            countryName: "United States",
            logo: doc.logo,
            flag: "us",
            complianceStatus: String(doc.shariahStates ?? ''),
            complianceRating: doc.ranking,
            textMatch: 0,
         };
      });
   }

   getEtfDataFromSymbols( symbols: string[] ): Observable<Record<string, EtfProfile>> {

      if (!symbols.length) {
         return of({});
      }

      const url =
         `${environment.typeSense.url}/collections/${TYPESENSE_COLLECTIONS.ETF_PROFILE}/documents/search`;

      const params = new HttpParams()
         .set("q", "*")
         .set("per_page", "250")
         // ✅ FIX: filter by SYMBOL, not id
         .set("filter_by", `symbol:=[${symbols.join(",")}]`);

      return this.http.get<any>(url, { params }).pipe(
         map(res => this.mapProfilesBySymbol(res)),
         catchError(err => {
            console.error("[ETF profile error]", err);
            return of({});
         })
      );
   }

   private mapProfilesBySymbol( response: any ): Record<string, EtfProfile> {

      const profiles: Record<string, EtfProfile> = {};

      for (const hit of response?.hits ?? []) {
         const doc = hit.document;

         profiles[doc.symbol] = {
            id: doc.id,
            symbol: doc.symbol,
            name: doc.name,
            exchange: doc.exchange,
            domicile: doc.domicile,
            aum: doc.aum,
            nav: doc.nav,
            navCurrency: doc.navCurrency,
            expenseRatio: doc.expenseRatio,
            assetClass: doc.assetClass,
            etfCompany: doc.etfCompany,
            inceptionDate: doc.inceptionDate,
            numberOfHoldings: doc.numberOfHoldings,
            trackingIndex: doc.trackingIndex,

            halal: doc.halal,
            notHalal: doc.notHalal,
            avoid: doc.avoid,
            unCovered: doc.unCovered,

            cusip: doc.cusip,
            isin: doc.isin,
            identifier: doc.identifier,
            mainSymbol: doc.mainSymbol,
            shariahStates: doc.shariahStates,
            investmentSegment: doc.investmentSegment,
            ranking: doc.ranking,
            description: doc.description
         };
      }

      return profiles;
   }
   
}
